import { useCallback, useContext, useEffect, useSyncExternalStore } from "react";
import { GlobalContext } from "../GlobalContext";

/**
 * Sheet row density, in two independent scopes.
 *
 *   "sheet" — the nine full-page sheets. "comfortable" (56px rows, 48px
 *             thumbnail) or "compact" (40px / 32px, roughly +50% records per
 *             screen). Toggled from SheetFooter.
 *   "grid"  — the four tables inside the vehicle modal (expenses, comments,
 *             payments, history). "compact" (32px) or "comfortable" (44px),
 *             and compact is the DEFAULT here, because these render no vehicle
 *             thumbnail and hold a handful of rows: the height-for-legibility
 *             trade the sheet scope exists to make has nothing to buy.
 *
 * The two scopes are separate because they used to be one, and that was a bug
 * you could see: the modal tables read <html data-sheet-density> but render no
 * SheetFooter, so toggling density on Inventory silently resized a table inside
 * a modal and nothing on that tab could undo it. Now each scope owns its own
 * key, its own attribute and its own store.
 *
 * Deliberately localStorage and NOT Users.Preferences: /edituserpreferences is
 * shaped strictly around { ownSheetName, FieldOrder }, and density is a
 * per-screen choice anyway — the same person wants different answers on a
 * laptop and a 27" monitor. The key is user-scoped like the vehicle drafts
 * (Inventory.jsx), so a shared machine doesn't leak one user's choice.
 *
 * The value is applied to <html> as a data-* attribute; the table CSS reads it,
 * so no table needs to subscribe. Each scope is mounted from more than one
 * place (CarsNav app-wide AND SheetFooter; three separate modal tabs), so the
 * state is an external store rather than per-instance — otherwise the copies
 * would drift apart.
 */
const VALUES = ["comfortable", "compact"];

/**
 * `attr` is the dataset key, so it lands on <html> as data-sheet-density /
 * data-grid-density. Kept as plain data rather than branching in the hook so
 * that adding a third scope is a line here and nothing else.
 */
export const SCOPES = {
    sheet: { prefix: "sheetDensity", attr: "sheetDensity", fallback: "comfortable" },
    grid: { prefix: "gridDensity", attr: "gridDensity", fallback: "compact" },
};

const DEFAULT_SCOPE = "sheet";

/** Pure, and exported for the unit tests: an unknown scope must not throw. */
export function scopeConfig(scope) {
    return SCOPES[scope] || SCOPES[DEFAULT_SCOPE];
}

/** Pure. The user-scoped localStorage key for a scope. */
export function densityStorageKey(scope, UserID) {
    return `${scopeConfig(scope).prefix}_u${UserID}`;
}

//One store per scope. Created lazily so the module has no start-up cost and so
//a scope nobody uses never allocates.
const stores = new Map();

const getStore = (scope) => {
    let store = stores.get(scope);
    if (store) return store;

    const listeners = new Set();
    store = {
        current: scopeConfig(scope).fallback,
        boundKey: null,
        listeners,
        //Stable identities: useSyncExternalStore resubscribes whenever these
        //change, so they are built once per scope rather than per render.
        subscribe: (fn) => { listeners.add(fn); return () => listeners.delete(fn); },
        snapshot: () => store.current,
    };
    stores.set(scope, store);
    return store;
};

const read = (key, fallback) => {
    try {
        const saved = localStorage.getItem(key);
        return VALUES.includes(saved) ? saved : fallback;
    } catch {
        return fallback;   //private mode / storage disabled
    }
};

const emit = (store) => { for (const l of store.listeners) l(); };

const bind = (store, key, fallback) => {
    if (store.boundKey === key) return;
    store.boundKey = key;
    store.current = read(key, fallback);
    emit(store);
};

export default function useSheetDensity(scope = DEFAULT_SCOPE) {
    const { UserID } = useContext(GlobalContext);
    //Normalise once, so a typo'd scope can never pair one scope's store with
    //another's config — which would write the right value under the wrong key.
    const name = SCOPES[scope] ? scope : DEFAULT_SCOPE;
    const { attr, fallback } = SCOPES[name];
    const storageKey = densityStorageKey(name, UserID);
    const store = getStore(name);

    //Re-read when the user changes (signing in as someone else without reload).
    if (store.boundKey !== storageKey) bind(store, storageKey, fallback);

    const density = useSyncExternalStore(store.subscribe, store.snapshot, () => fallback);

    useEffect(() => {
        document.documentElement.dataset[attr] = density;
    }, [attr, density]);

    const toggle = useCallback(() => {
        const next = store.current === "compact" ? "comfortable" : "compact";
        store.current = next;
        try { localStorage.setItem(storageKey, next); } catch { /* non-fatal */ }
        emit(store);
    }, [store, storageKey]);

    return [density, toggle];
}
